import React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useGlobalCanvas } from '../contexts/GlobalCanvasContext';
import EnhancedTabBar from './EnhancedTabBar';
import ContentRenderer from './ContentRenderer';

/**
 * Props for the ResizablePanes component
 * @interface ResizablePanesProps
 */
interface ResizablePanesProps {
  /** Optional callback fired when a task is updated within a pane */
  onUpdateTask?: () => void;
  /** Optional callback fired when an email is updated within a pane */
  onUpdateEmail?: () => void;
}

/**
 * Safely retrieves a property from a view object with a fallback default value
 * Prevents errors when accessing properties on potentially undefined/null view objects
 *
 * @param {any} view - The view object to extract property from
 * @param {string} property - The property name to retrieve
 * @param {any} defaultValue - Default value to return if property is undefined
 * @returns {any} The property value or default value
 *
 * @example
 * ```typescript
 * const size = getViewProperty(view, 'size', 50); // Returns view.size or 50 if undefined
 * ```
 */
const getViewProperty = (view: any, property: string, defaultValue: any = null) => {
  return view && view[property] !== undefined ? view[property] : defaultValue;
};

/**
 * ResizablePanes component for managing multiple canvas views with drag-and-drop resizing
 * Supports single pane, horizontal/vertical splits, and complex nested layouts
 * Uses react-resizable-panels for smooth resize interactions
 *
 * @component
 * @param {ResizablePanesProps} props - Component props
 * @returns {JSX.Element} Rendered resizable pane layout
 *
 * @example
 * ```tsx
 * <ResizablePanes
 *   onUpdateTask={() => console.log('Task updated')}
 *   onUpdateEmail={() => console.log('Email updated')}
 * />
 * ```
 *
 * Features:
 * - Drag-to-resize panel boundaries
 * - Support for horizontal and vertical splits
 * - Nested panel group layouts
 * - Minimum size constraints (20%)
 * - Keyboard shortcuts: Ctrl+T (new tab), Ctrl+\ (split), drag tabs to reorder
 */
const ResizablePanes: React.FC<ResizablePanesProps> = ({
  onUpdateTask,
  onUpdateEmail
}) => {
  const { splitViews, resizeSplitView } = useGlobalCanvas();

  /**
   * Renders the content for a specific pane based on the active tab
   * Finds the active tab in the view's tab array and renders it using ContentRenderer
   *
   * @param {any} view - The view object containing tabs and active tab information
   * @returns {JSX.Element} Rendered content for the active tab
   *
   * @example
   * ```typescript
   * const view = { tabs: [...], activeTabId: 'tab-1' };
   * const content = renderPaneContent(view); // Renders content for active tab
   * ```
   */
  const renderPaneContent = (view: any) => {
    const activeTab = view?.tabs?.find((tab: any) => tab.id === view.activeTabId);

    return (
      <ContentRenderer
        tab={activeTab}
        onUpdateTask={onUpdateTask}
        onUpdateEmail={onUpdateEmail}
      />
    );
  };

  /**
   * Renders a single split view as a resizable panel
   * Creates a Panel component with resize handling and content rendering
   * Includes EnhancedTabBar for tab navigation and content area
   *
   * @param {any} view - The view object to render as a panel
   * @returns {JSX.Element|null} Rendered panel component or null if view is invalid
   *
   * @example
   * ```typescript
   * const view = { id: 'view-1', size: 50, tabs: [...] };
   * const panel = renderSplitView(view); // Renders resizable panel with tabs
   * ```
   */
  const renderSplitView = (view: any) => {
    if (!view || !view.id) return null;

    return (
      <Panel
        key={view.id}
        defaultSize={getViewProperty(view, 'size', 50)}
        minSize={20}
        onResize={(size) => resizeSplitView && resizeSplitView(view.id, size)}
        className="obsidian-panel"
      >
        <div className="obsidian-split-view">
          <EnhancedTabBar view={view} />
          <div className="obsidian-content-area">
            {renderPaneContent(view)}
          </div>
        </div>
      </Panel>
    );
  };

  // Handle empty state and null checks
  if (!splitViews || splitViews.length === 0) {
    return (
      <div className="obsidian-empty-state">
        <div className="obsidian-empty-state-icon">🚀</div>
        <h3 className="obsidian-empty-state-title">Welcome to LexAI Canvas</h3>
        <p className="obsidian-empty-state-description">
          Press <kbd>Ctrl+T</kbd> to create your first canvas, or open content from the sidebar.
        </p>
        <p className="obsidian-empty-state-subtitle">
          Drag tabs to reorder • <kbd>Ctrl+\</kbd> to split panes • Drag tabs outside to pop out
        </p>
      </div>
    );
  }

  // Single pane - no need for panel group
  if (splitViews.length === 1) {
    const view = splitViews[0];
    if (!view) return null;

    console.log('🎨 ResizablePanes rendering single view:', {
      viewId: view.id,
      tabCount: view.tabs?.length || 0,
      tabs: view.tabs?.map(t => ({ id: t.id, title: t.title, type: t.type }))
    });

    return (
      <div className="obsidian-split-view">
        <EnhancedTabBar view={view} />
        <div className="obsidian-content-area">
          {renderPaneContent(view)}
        </div>
      </div>
    );
  }

  // Multiple panes - use resizable panels
  const horizontalViews = splitViews.filter(v => v && (v.direction === 'horizontal' || !v.direction));
  const verticalViews = splitViews.filter(v => v && v.direction === 'vertical');

  if (verticalViews.length > 0 && horizontalViews.length > 0) {
    // Mixed layout - nest panel groups
    return (
      <PanelGroup direction="horizontal" className="obsidian-panel-group">
        {horizontalViews.map((view, index) => (
          <React.Fragment key={view?.id || index}>
            {renderSplitView(view)}
            {index < horizontalViews.length - 1 && (
              <PanelResizeHandle className="obsidian-resize-handle" />
            )}
          </React.Fragment>
        ))}

        {horizontalViews.length > 0 && verticalViews.length > 0 && (
          <PanelResizeHandle className="obsidian-resize-handle" />
        )}

        {verticalViews.length > 0 && (
          <Panel defaultSize={50} minSize={20} className="obsidian-panel">
            <PanelGroup direction="vertical" className="obsidian-panel-group">
              {verticalViews.map((view, index) => (
                <React.Fragment key={view?.id || index}>
                  {renderSplitView(view)}
                  {index < verticalViews.length - 1 && (
                    <PanelResizeHandle className="obsidian-resize-handle obsidian-resize-handle-vertical" />
                  )}
                </React.Fragment>
              ))}
            </PanelGroup>
          </Panel>
        )}
      </PanelGroup>
    );
  }

  // All same direction
  const direction = verticalViews.length > 0 ? 'vertical' : 'horizontal';
  const viewsToRender = verticalViews.length > 0 ? verticalViews : horizontalViews;

  if (!viewsToRender || viewsToRender.length === 0) {
    return null;
  }

  return (
    <PanelGroup direction={direction} className="obsidian-panel-group">
      {viewsToRender.map((view, index) => (
        <React.Fragment key={view?.id || index}>
          {renderSplitView(view)}
          {index < viewsToRender.length - 1 && (
            <PanelResizeHandle
              className={`obsidian-resize-handle ${direction === 'vertical' ? 'obsidian-resize-handle-vertical' : ''}`}
            />
          )}
        </React.Fragment>
      ))}
    </PanelGroup>
  );
};

export default ResizablePanes;
